/**
 * Centralized validation utilities for barcode formats
 */

/**
 * Validate bundle barcode format
 * Valid formats:
 *   • Salem (legacy/default): `BUN######/##-##`     — 15 chars, e.g. BUN000001/25-26
 *   • Site-prefixed:          `<SITE>-BUN######/##-##` — e.g. SVPR-BUN000001/25-26, CHN-BUN000001/25-26
 *   The optional site prefix is 2–5 uppercase letters followed by a hyphen,
 *   issued from `sites_registry.code_prefix` for non-default sites.
 *
 * @param {string} code - The barcode to validate
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validateBundleFormat = (code) => {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'Bundle barcode is required' };
  }

  const trimmed = code.trim();

  // Must contain BUN segment
  if (!trimmed.includes('BUN')) {
    return { valid: false, error: 'Bundle code must contain "BUN"' };
  }

  // Accept both legacy + site-prefixed formats
  const bundlePattern = /^(?:[A-Z]{2,5}-)?BUN\d{6}\/\d{2}-\d{2}$/;
  if (!bundlePattern.test(trimmed)) {
    return {
      valid: false,
      error: 'Invalid bundle format. Expected: BUN000001/25-26 or <SITE>-BUN000001/25-26'
    };
  }

  return { valid: true, error: null };
};

/**
 * Quick check if a code looks like a bundle code
 * @param {string} code
 * @returns {boolean}
 */
export const isBundleCode = (code) => {
  if (!code) return false;
  return /^(?:[A-Z]{2,5}-)?BUN\d{6}\/\d{2}-\d{2}$/.test(code.trim());
};

/**
 * Quick check if a code looks like a USN code
 * USN codes typically don't start with BUN and are 8-10 characters
 * @param {string} code 
 * @returns {boolean}
 */
export const isUsnCode = (code) => {
  if (!code) return false;
  const trimmed = code.trim();
  return !trimmed.startsWith('BUN') && trimmed.length >= 6 && trimmed.length <= 12;
};

/**
 * Validate document barcode format
 * Valid format: DOC/##-##/#### (e.g., DOC/25-26/0001)
 * @param {string} code 
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validateDocumentFormat = (code) => {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'Document barcode is required' };
  }

  const trimmed = code.trim();
  
  if (!trimmed.startsWith('DOC/')) {
    return { valid: false, error: 'Document code must start with "DOC/"' };
  }

  const docPattern = /^DOC\/\d{2}-\d{2}\/\d{4}$/;
  if (!docPattern.test(trimmed)) {
    return { valid: false, error: 'Invalid document format. Expected: DOC/25-26/0001' };
  }

  return { valid: true, error: null };
};
